'use client';

import { useState } from "react";
import FooterOverlay from "./FooterOverlay";
import "../../styles/ReusableStyles/Footer.css";
import SubscribeButton from "./SubscribeButton";
import Logo from "./Logo";
import NavigationArrows from "./NavigationArrows";

// Assets in /public
const FOOTER_GIF = "/Footer.gif";
const FOOTER_MOBILE = "/FooterMobile.png";
const SITE = "https://natoe.ai";

// Inline brand icons (replace FontAwesome; styled by .social-icons svg via currentColor/1em)
const InLinkedIn = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" aria-hidden="true"><path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5zM3 9h4v12H3zM10 9h3.8v1.7h.05c.53-1 1.83-2.05 3.77-2.05 4.03 0 4.78 2.65 4.78 6.1V21h-4v-5.4c0-1.29-.02-2.95-1.8-2.95-1.8 0-2.08 1.4-2.08 2.85V21h-4z"/></svg>
);
const InYouTube = () => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" aria-hidden="true"><path d="M23 12s0-3.2-.4-4.7a2.5 2.5 0 0 0-1.76-1.77C19.34 5.1 12 5.1 12 5.1s-7.34 0-8.84.43A2.5 2.5 0 0 0 1.4 7.3C1 8.8 1 12 1 12s0 3.2.4 4.7a2.5 2.5 0 0 0 1.76 1.77C4.66 18.9 12 18.9 12 18.9s7.34 0 8.84-.43a2.5 2.5 0 0 0 1.76-1.77C23 15.2 23 12 23 12zM9.75 15.02v-6.04L15.5 12z"/></svg>
);
const InX = () => (
  <svg viewBox="0 0 24 24" width="0.9em" height="0.9em" fill="currentColor" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.65l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
);

const Footer = () => {
  const [email, setEmail] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [emailError, setEmailError] = useState("");

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (!email) { setEmailError("Email is required"); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) { setEmailError("Please enter a valid email address"); return; }
    setEmailError("");
    setShowSuccess(true);
    setEmail("");
    setTimeout(() => setShowSuccess(false), 2000);
  };

  return (
    <footer className="footer">
      <div className="footer-content">
        {/* First Row: Logo and Newsletter */}
        <div className="footer-row">
          <div className="footer-logo">
            <Logo />
          </div>
          <div className="subscribe-box">
            <div className="subscribe-input">
              <div className="input-container">
                <input
                  type="email"
                  placeholder="Email"
                  aria-label="Email address for newsletter subscription"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setEmailError(""); }}
                  className={emailError ? "newsletter-input-error" : ""}
                />
                <SubscribeButton text="Subscribe" onClick={handleSubscribe} />
              </div>
              {emailError && <span className="newsletter-error-message">{emailError}</span>}
            </div>
          </div>
        </div>

        {/* Second Row: Navigation, Contact, Social, and Arrow */}
        <div className="footer-row">
          <nav className="footer-nav">
            <nav className="footer-nav-col">
              <ul>
                <li><a href={SITE}>Home</a></li>
                <li>
                  <h4>Platform</h4>
                  <ul className="footer-submenu">
                    <li><a href={`${SITE}/platform/imaging-centers`}>Imaging Centers</a></li>
                    <li><a href={`${SITE}/platform/radiologists`}>Radiologists</a></li>
                  </ul>
                </li>
                <li><a href={`${SITE}/technology`}>Technology</a></li>
              </ul>
            </nav>
            <nav className="footer-nav-col">
              <ul>
                <li>
                  <h4>Services</h4>
                  <ul className="footer-submenu">
                    <li><a href={`${SITE}/services/ct-reading`}>CT Reading</a></li>
                    <li><a href={`${SITE}/services/mri-reading`}>MRI Reading</a></li>
                    <li><a href={`${SITE}/services/xray-reading`}>X-Ray Reading</a></li>
                    <li><a href={`${SITE}/services/mammography`}>Mammography</a></li>
                    <li><a href={`${SITE}/services/neuroradiology`}>Neuroradiology</a></li>
                  </ul>
                </li>
              </ul>
            </nav>
            <nav className="footer-nav-col">
              <ul>
                <li>
                  <h4>Insights</h4>
                  <ul className="footer-submenu">
                    <li><a href={`${SITE}/blog`}>Blog</a></li>
                    <li><a href={`${SITE}/webinar`}>Webinars</a></li>
                  </ul>
                </li>
                <li><a href={`${SITE}/pricing`}>Pricing</a></li>
                <li><a href={`${SITE}/trust`}>Trust &amp; Security</a></li>
                <li><a href={`${SITE}/about`}>About</a></li>
                <li><a href={`${SITE}/careers`}>Careers</a></li>
                <li><a href={`${SITE}/reachUs`}>Contact</a></li>
              </ul>
            </nav>
          </nav>

          <div className="contact-info">
            <div>
              <h4>Contact Us</h4>
              <div className="contact-item">
                <span>+1 (656) 241-6730</span>
              </div>
            </div>
            <div>
              <h4>Email</h4>
              <div className="contact-item contact-email">
                <span>contact@natoe.ai</span>
              </div>
            </div>
            <div>
              <h4>Office</h4>
              <div className="contact-item">
                <address style={{ fontStyle: 'normal' }}>29399 US Highway 19 N, Suite #150, Clearwater, Florida 33761</address>
              </div>
            </div>
            <div className="copyright">
              <span>© 2026 NATOE. All rights reserved.</span>
            </div>
          </div>

          <div className="footer-row-2nd">
            <div className="social-links">
              <h4>Follow Us</h4>
              <div className="social-icons">
                <a href="https://www.linkedin.com/company/natoe-ai/" target="_blank" rel="noopener noreferrer" aria-label="Follow Natoe AI on LinkedIn"><InLinkedIn /></a>
                <a href="https://www.youtube.com/@NatoeAI" target="_blank" rel="noopener noreferrer" aria-label="Subscribe to Natoe AI on YouTube"><InYouTube /></a>
                <a href="https://x.com/Natoe_AI" target="_blank" rel="noopener noreferrer" aria-label="Follow Natoe AI on X"><InX /></a>
              </div>
            </div>
            <div className="privacy-policy-desktop privacy-policy">
              <a href={`${SITE}/privacy-policy`}>Privacy Policy</a>
              <span className="footer-link-separator">|</span>
              <a href={`${SITE}/terms-and-conditions`}>T&amp;C</a>
            </div>
          </div>

          <div className="scroll-to-top">
            <NavigationArrows onUp={scrollToTop} />
          </div>
        </div>
      </div>

      {/* Success Popup */}
      {showSuccess && (
        <div className="success-popup-overlay">
          <div className="success-popup">
            <h3>Thank You!</h3>
            <p>You have successfully subscribed to our newsletter.</p>
          </div>
        </div>
      )}

      {/* Footer GIF */}
      <div className="footer-gif desktop">
        <img src={FOOTER_GIF} alt="Footer Animation" loading="lazy" />
        <FooterOverlay />
      </div>

      {/* Mobile Footer */}
      <div className="footer-gif mobile">
        <img src={FOOTER_MOBILE} alt="Footer Animation" loading="lazy" />
        <FooterOverlay />
        <div className="mobile-copyright">
          <span>© 2026 NATOE. All rights reserved.</span>
          <span className="privacy-policy-mobile privacy-policy">
            <a href={`${SITE}/privacy-policy`}>Privacy Policy</a>
            <span className="footer-link-separator">|</span>
            <a href={`${SITE}/terms-and-conditions`}>T&amp;C</a>
          </span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
